import { Fragment } from "react";
import { BarChart3, AlertTriangle, Film, Clock } from "lucide-react";
import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { cn, calculateDelta, message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type CreativesScorecardParams } from "@/apis/brand-os/brand-governance";
import type { CreativesScorecardBrandScorecard } from "@/schema/brand-os/brand-governance";

interface StatCardProps {
  icon: React.ElementType;
  iconClassName: string;
  value: string;
  primaryLabel: string;
  secondaryLabel: string;
  delta: string | null;
}
function cardConfig(scorecard: CreativesScorecardBrandScorecard): StatCardProps[] {
  const { overall_score, below_threshold_count, total_creatives, format_breakdown, time_to_publish } = scorecard;
  const breakdown = format_breakdown.breakdown ?? format_breakdown.prev_breakdown;
  const videoRatioPct = Math.round((breakdown?.video_ratio ?? 0) * 100);
  const total = total_creatives.count;
  const below = below_threshold_count.count;

  return [
    {
      icon: BarChart3,
      iconClassName: "bg-blue-50 text-blue-600",
      value: (overall_score.score ?? 0).toFixed(2),
      primaryLabel: "Avg Compliance",
      secondaryLabel: `Across ${total} creatives`,
      delta: calculateDelta(overall_score.prev_score, overall_score.score ?? 0),
    },
    {
      icon: AlertTriangle,
      iconClassName: "bg-amber-50 text-amber-600",
      value: String(below),
      primaryLabel: "Sub-80 Creatives",
      secondaryLabel: `${total - below} above threshold`,
      delta: calculateDelta(below_threshold_count.prev_count, below_threshold_count.count),
    },
    {
      icon: Film,
      iconClassName: "bg-violet-50 text-violet-600",
      value: `${videoRatioPct}:${100 - videoRatioPct}`,
      primaryLabel: "Video : Static",
      secondaryLabel: "Industry benchmark 55:45",
      delta: null,
    },
    {
      icon: Clock,
      iconClassName: "bg-teal-50 text-teal-600",
      value: `${time_to_publish.value.toFixed(1)}d`,
      primaryLabel: "Time-to-Publish",
      secondaryLabel: "Benchmark: 6 days",
      delta: calculateDelta(time_to_publish.prev_value, time_to_publish.value),
    },
  ];
}

function StatsComponent({ status, brandScorecard }: { status: QueryStatus; brandScorecard?: CreativesScorecardBrandScorecard | null }) {
  switch (status) {
    case "pending":
      return <StatCardSkeleton />;
    case "error":
      return <LockedCard message={message} className="col-span-full" />;
    case "success":
      return brandScorecard ? <StatsContent brandScorecard={brandScorecard} /> : <LockedCard message={message} className="col-span-full"/>;
  }
}

export function Stats({ params }: { params: CreativesScorecardParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.CreativesScorecard(params));

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatsComponent status={status} brandScorecard={data?.data.brand_scorecard} />
    </div>
  );
}

function StatsContent({ brandScorecard }: { brandScorecard: CreativesScorecardBrandScorecard }) {
  const cards = cardConfig(brandScorecard);

  return (
    <Fragment>
      {cards.map((card) => (
        <Card key={card.primaryLabel}>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className={cn("flex size-10 items-center justify-center rounded-lg", card.iconClassName)}>
                  <card.icon className="size-5" />
                </div>
                <span className="text-xs font-medium text-destructive">{card.delta}</span>
              </div>
              <p className="text-3xl font-semibold text-foreground">{card.value}</p>
              <div>
                <p className="text-sm font-medium text-foreground/60">{card.primaryLabel}</p>
                <p className="text-sm text-muted-foreground/50">{card.secondaryLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </Fragment>
  );
}

function StatCardSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i}>
          <CardContent className="relative">
            <Skeleton className="absolute right-4 top-4 h-4 w-10 rounded-md" aria-hidden />
            <div className="flex flex-col gap-3">
              <Skeleton className="size-10 shrink-0 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-md" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-28 rounded-md" />
                <Skeleton className="h-4 w-36 rounded-md" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </Fragment>
  );
}
